import React from 'react';
import {Alert, Box, CircularProgress, List, Typography} from '@mui/material';
import SuggestedReferenceItem from './SuggestedReferenceItem';

/**
 * Suggested references tab — V4 backend suggestions only.
 */
export default function SuggestedReferencesTab({
    suggestedReferencesStatus = 'idle',
    suggestedReferencesError = null,
    suggestedCitations = [],
    addedLocalIds = new Set(),
    claimsStatus = 'idle',
    claimsCount = 0,
    cfg,
    onAddReference,
    onRemoveReference,
}) {
    const hasSuggestions = suggestedCitations.length > 0;
    const extractionInProgress = claimsStatus === 'extracting' || claimsStatus === 'verifying';

    const sorted = (() => {
        const added = suggestedCitations.filter((c) => addedLocalIds.has(c.localId));
        const rest = suggestedCitations.filter((c) => !addedLocalIds.has(c.localId));
        return [...added, ...rest];
    })();

    return (
        <Box>
            {(suggestedReferencesStatus === 'pending' || suggestedReferencesStatus === 'analysing') && (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, px: 2, pt: 2, pb: 1}}>
                    <CircularProgress size={14} />
                    <Typography variant="body2" sx={{color: 'text.secondary', fontSize: '0.75rem'}}>
                        {suggestedReferencesStatus === 'pending'
                            ? 'Preparing evidence search…'
                            : 'Finding suggestions, contradictions, and corrections…'}
                    </Typography>
                </Box>
            )}

            {suggestedReferencesStatus === 'error' && (
                <Box sx={{px: 2, pt: 2, pb: 1}}>
                    <Alert severity="error" sx={{fontSize: '0.72rem'}}>
                        {suggestedReferencesError || 'An error occurred while finding references.'}
                    </Alert>
                </Box>
            )}

            {extractionInProgress && !hasSuggestions && (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, px: 2, pt: 2, pb: 1}}>
                    <CircularProgress size={14} />
                    <Typography variant="body2" sx={{color: 'text.secondary', fontSize: '0.75rem'}}>
                        {claimsStatus === 'extracting' ? 'Extracting claims…' : 'Verifying claims…'}
                    </Typography>
                </Box>
            )}

            {hasSuggestions && (
                <List sx={{display: 'flex', flexDirection: 'column', pt: 0, pb: 0}}>
                    {sorted.map((citation, idx) => (
                        <SuggestedReferenceItem
                            key={citation.localId || citation.referenceId || idx}
                            citation={citation}
                            isAdded={addedLocalIds.has(citation.localId)}
                            cfg={cfg}
                            onAdd={onAddReference}
                            onRemove={onRemoveReference}
                        />
                    ))}
                </List>
            )}

            {!extractionInProgress
                && suggestedReferencesStatus !== 'pending'
                && suggestedReferencesStatus !== 'analysing'
                && !hasSuggestions
                && suggestedReferencesStatus !== 'error' && (
                <Typography variant="body2" sx={{color: 'text.secondary', p: 2}}>
                    {claimsCount > 0
                        ? 'No supporting references found for these claims.'
                        : claimsStatus === 'idle'
                            ? 'Claims not yet extracted for this passage.'
                            : 'No suggestions available.'}
                </Typography>
            )}
        </Box>
    );
}
